import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { TabView, TabPanel } from 'primereact/tabview';
import { MultiSelect } from 'primereact/multiselect';
import {
  StepViewModel,
  SimpleExpression,
  LogicalOperator,
  WaterfallRuleType,
  WaterfallStepAction,
  OverrideSettingViewModel,
  WaterfallRuleViewModel,
  IntegrationType,
  ProviderSelectionStrategy,
  MultipleStepExecutionMode,
} from '../../models/waterfall';
import { MetadataContextType, FieldMetadata, OperatorInfo } from '../../models/expression';
import IntegrationSettingsService from '../../services/integrationSettingsService';
import { IntegrationSettings } from '../../models/integrationSettings';
import * as expressionService from '../../services/expressionService';
import * as configurationService from '../../services/configurationService';
import { mergeCustomFieldsWithMetadata } from '../../utils/custom-field-helper';
import { RuleGroup } from './RuleGroup';
import { OverrideSettingForm } from './OverrideSettingForm';
import { filterFieldsByVendor } from './stepUtils';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import FormInputNumber from '../../components/form/FormInputNumber';
import FormRadioGroup from '../../components/form/FormRadioGroup';
import { noop } from '../../utils/misc-utils';

interface StepDetailsDialogProps {
  step: StepViewModel | null;
  visible: boolean;
  onClose: () => void;
  onSave: (step: StepViewModel) => Promise<void> | void;
  integrationType: IntegrationType;
}

interface StepFormValues {
  description: string;
  integrationSettingsIds: number[];
  selectionStrategy?: ProviderSelectionStrategy | null;
  stepTimeoutInMinutes?: number | null;
  onTimeoutExceededAction?: WaterfallStepAction;
  onFatalErrorAction?: WaterfallStepAction;
  multipleStepExecutionMode?: MultipleStepExecutionMode;
}

// Action options for radio groups
const actionOptions = [
  { label: 'Abort', value: WaterfallStepAction.Abort },
  { label: 'Continue', value: WaterfallStepAction.Continue },
];

// Selection strategy options
const selectionStrategyOptions = [
  { label: 'Round Robin', value: ProviderSelectionStrategy.RoundRobin },
];

// Multiple step execution mode options
const multipleStepExecutionModeOptions = [
  { label: 'All', value: MultipleStepExecutionMode.All },
  { label: 'First', value: MultipleStepExecutionMode.First },
];

/** Returns true if the expression has no valid content (empty expressions array or all items empty) */
const isExpressionEmpty = (expr: SimpleExpression | null | undefined): boolean => {
  if (!expr?.expressions?.length) return true;
  return !expr.expressions.some(
    (item) =>
      (item.expression && item.expression.left && item.expression.operator && item.expression.right) ||
      (item.expressionGroup && !isExpressionEmpty(item.expressionGroup))
  );
};

const validationSchema = yup.object().shape({
  description: yup.string().trim().required('Step name is required'),
  integrationSettingsIds: yup
    .array()
    .of(yup.number().required())
    .min(1, 'At least one vendor is required')
    .required('At least one vendor is required'),
  selectionStrategy: yup
    .mixed<ProviderSelectionStrategy>()
    .nullable()
    .when('integrationSettingsIds', {
      is: (ids: number[]) => ids && ids.length > 1,
      then: (schema) => schema.required('Selection strategy is required when multiple vendors are selected'),
      otherwise: (schema) => schema.nullable(),
    }),
  stepTimeoutInMinutes: yup.number().nullable().min(1, 'Must be at least 1 minute').optional(),
  onTimeoutExceededAction: yup.mixed<WaterfallStepAction>().oneOf(Object.values(WaterfallStepAction)).optional(),
  onFatalErrorAction: yup.mixed<WaterfallStepAction>().oneOf(Object.values(WaterfallStepAction)).optional(),
  multipleStepExecutionMode: yup.mixed<MultipleStepExecutionMode>().oneOf(Object.values(MultipleStepExecutionMode)).optional(),
});

export const StepDetailsDialog: FC<StepDetailsDialogProps> = ({ step, visible, onClose, onSave, integrationType }) => {
  const [integration, setIntegration] = useState<IntegrationSettings | null>(null);
  const [isLoadingIntegration, setIsLoadingIntegration] = useState(false);
  const [hasRuleChanges, setHasRuleChanges] = useState(false);
  const [hasExistingRules, setHasExistingRules] = useState(false);
  const isInitialLoad = useRef(true);
  const [vendorOptions, setVendorOptions] = useState<{ label: string; value: number }[]>([]);
  const [waterfallContextFields, setWaterfallContextFields] = useState<FieldMetadata[]>([]);
  const [requestValueTargetFields, setRequestValueTargetFields] = useState<FieldMetadata[]>([]);
  const [operators, setOperators] = useState<OperatorInfo[]>([]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
    watch,
  } = useForm<StepFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      description: '',
      integrationSettingsIds: [],
      selectionStrategy: null,
      stepTimeoutInMinutes: undefined,
      onTimeoutExceededAction: WaterfallStepAction.Continue,
      onFatalErrorAction: WaterfallStepAction.Continue,
    },
  });

  const [rules, setRules] = useState<WaterfallRuleViewModel[]>([]);
  const [ruleKeys, setRuleKeys] = useState<string[]>([]);
  const [ruleTabStates, setRuleTabStates] = useState<{ [key: string]: number }>({});
  const ruleKeyCounterRef = useRef(0);
  const addRuleMenuRef = useRef<Menu>(null);

  const generateRuleKey = () => `rule-${++ruleKeyCounterRef.current}`;
  
  const selectedVendorIds = watch('integrationSettingsIds');
  // For backward compatibility with integration loading, use the first selected vendor
  const selectedVendorId = selectedVendorIds && selectedVendorIds.length > 0 ? selectedVendorIds[0] : 0;

  const filteredWaterfallContextFields = useMemo(() => {
    if (!waterfallContextFields.length) {
      return waterfallContextFields;
    }

    if (!integration) {
      return filterFieldsByVendor(waterfallContextFields, null);
    }

    const vendorName = integration.vendor;
    if (!vendorName) {
      return filterFieldsByVendor(waterfallContextFields, null);
    }

    return filterFieldsByVendor(waterfallContextFields, vendorName);
  }, [integration, waterfallContextFields]);

  const filteredRequestValueTargetFields = useMemo(() => {
    if (!requestValueTargetFields.length) {
      return requestValueTargetFields;
    }

    if (!integration) {
      return filterFieldsByVendor(requestValueTargetFields, null);
    }

    const vendorName = integration.vendor;
    if (!vendorName) {
      return filterFieldsByVendor(requestValueTargetFields, null);
    }

    return filterFieldsByVendor(requestValueTargetFields, vendorName);
  }, [integration, requestValueTargetFields]);

  useEffect(() => {
    if (!isInitialLoad.current) {
      setHasRuleChanges(true);
    }
  }, [rules]);

  useEffect(() => {
    if (visible) {
      const fetchMetadata = async () => {
        try {
          const [fields, requestValueTargetFields, allOperators, customFields] = await Promise.all([
            expressionService.getFieldMetadata(MetadataContextType.WaterfallContext),
            expressionService.getFieldMetadata(MetadataContextType.WaterfallRequestValueTarget),
            expressionService.getOperators(),
            configurationService.getAllCustomFields(),
          ]);

          const mergedWaterfallFields = mergeCustomFieldsWithMetadata(fields, customFields);
          const mergedRequestValueFields = mergeCustomFieldsWithMetadata(requestValueTargetFields, customFields);

          setWaterfallContextFields(mergedWaterfallFields);
          setRequestValueTargetFields(mergedRequestValueFields);

          // TODO: "Not", "And", "Or" are logical operators - backend strips binary expressions using them. Use Equal/NotEqual/etc for comparisons.
          const filteredOperators = allOperators.filter(
            (op) => op.isLogical && !['Not', 'And', 'Or'].includes(op.operator)
          );
          setOperators(filteredOperators);
        } catch (error) {
          console.error('Failed to fetch field metadata:', error);
        }
      };
      
      fetchMetadata();
    }
  }, [visible, integration]);

  useEffect(() => {
    if (visible && integrationType && integrationType !== IntegrationType.None) {
      const fetchVendors = async () => {
        try {
          const settingsList = await IntegrationSettingsService.getInstance().getByType(integrationType);
          const options = settingsList
            .map((settings) => ({
              label: settings.name,
              value: settings.integrationSettingsId!,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setVendorOptions(options);
        } catch (error) {
          console.error('Failed to fetch integration settings:', error);
          setVendorOptions([]);
        }
      };
      fetchVendors();
    }
  }, [visible, integrationType]);

  useEffect(() => {
    if (step && step.integrationSettingsIds && step.integrationSettingsIds.length > 0) {
      setIsLoadingIntegration(true);
      IntegrationSettingsService.getInstance()
        .get(step.integrationSettingsIds[0].toString())
        .then(setIntegration)
        .catch(() => {
          setIntegration(null);
        })
        .finally(() => setIsLoadingIntegration(false));
    } else {
      setIntegration(null);
    }

    if (step?.waterfallRules && step.waterfallRules.length > 0) {
      setRules(step.waterfallRules.map((rule) => ({ ...rule })));
      setRuleKeys(step.waterfallRules.map(() => `rule-${++ruleKeyCounterRef.current}`));
      setHasExistingRules(true);
    } else {
      setRules([]);
      setRuleKeys([]);
      setHasExistingRules(false);
    }

    if (step) {
      reset({
        description: step.description || '',
        integrationSettingsIds: step.integrationSettingsIds || [],
        selectionStrategy: step.selectionStrategy || null,
        stepTimeoutInMinutes: step.stepTimeoutInMinutes || undefined,
        onTimeoutExceededAction: step.onTimeoutExceededAction || WaterfallStepAction.Continue,
        onFatalErrorAction: step.onFatalErrorAction || WaterfallStepAction.Continue,
        multipleStepExecutionMode: step.multipleStepExecutionMode || MultipleStepExecutionMode.First,
      });
      setHasRuleChanges(false);
      isInitialLoad.current = true;
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 0);
    }
  }, [step, reset]);

  useEffect(() => {
    if (selectedVendorId && selectedVendorId > 0) {
      setIsLoadingIntegration(true);
      IntegrationSettingsService.getInstance()
        .get(selectedVendorId.toString())
        .then(setIntegration)
        .catch(() => {
          setIntegration(null);
        })
        .finally(() => setIsLoadingIntegration(false));
    } else if (selectedVendorId === 0) {
      setIntegration(null);
    }
  }, [selectedVendorId]);

  const handleAddRule = (ruleType: WaterfallRuleType) => {
    const newRule: WaterfallRuleViewModel = {
      ruleType,
      expression: ruleType === WaterfallRuleType.RequestSetting 
        ? null 
        : {
            logicalOperator: LogicalOperator.And,
            expressions: null,
            isNot: false,
          },
      overrideSetting: ruleType === WaterfallRuleType.RequestSetting
        ? { fieldName: null, value: null }
        : null,
    };
    setRules([...rules, newRule]);
    setRuleKeys([...ruleKeys, generateRuleKey()]);
    setHasRuleChanges(true);
  };

  const handleUpdateRuleExpression = (index: number, expression: SimpleExpression) => {
    const rule = rules[index];
    const isConditionRule =
      rule.ruleType === WaterfallRuleType.StepCondition || rule.ruleType === WaterfallRuleType.ExitCondition;

    if (isConditionRule && isExpressionEmpty(expression)) {
      const updatedRules = rules.filter((_, i) => i !== index);
      const updatedKeys = ruleKeys.filter((_, i) => i !== index);
      setRules(updatedRules);
      setRuleKeys(updatedKeys);
    } else {
      const updatedRules = [...rules];
      updatedRules[index] = {
        ...updatedRules[index],
        expression,
      };
      setRules(updatedRules);
    }
    setHasRuleChanges(true);
  };

  const handleUpdateRuleOverrideSetting = (index: number, overrideSetting: OverrideSettingViewModel | null) => {
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      overrideSetting: overrideSetting || null,
    };
    setRules(updatedRules);
    setHasRuleChanges(true);
  };

  const handleDeleteRule = (index: number) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    const updatedKeys = ruleKeys.filter((_, i) => i !== index);
    setRules(updatedRules);
    setRuleKeys(updatedKeys);
    setHasRuleChanges(true);
  };

  const getRuleTypeLabel = (ruleType: WaterfallRuleType): string => {
    switch (ruleType) {
      case WaterfallRuleType.StepCondition:
        return 'Step Condition';
      case WaterfallRuleType.ExitCondition:
        return 'Exit Condition';
      case WaterfallRuleType.RequestSetting:
        return 'Request Setting';
      default:
        return 'Unknown';
    }
  };

  const addRuleMenuItems = [
    {
      label: 'Step Condition',
      icon: 'pi pi-filter',
      command: () => handleAddRule(WaterfallRuleType.StepCondition),
    },
    {
      label: 'Exit Condition',
      icon: 'pi pi-sign-out',
      command: () => handleAddRule(WaterfallRuleType.ExitCondition),
    },
    {
      label: 'Request Setting',
      icon: 'pi pi-cog',
      command: () => handleAddRule(WaterfallRuleType.RequestSetting),
    },
  ];

  const handleFormSubmit = async (data: StepFormValues) => {
    if (!step) return;

    const validRules = rules.filter((rule) => {
      if (rule.ruleType === WaterfallRuleType.RequestSetting) {
        return rule.overrideSetting && rule.overrideSetting.fieldName && rule.overrideSetting.value;
      } else {
        return !isExpressionEmpty(rule.expression);
      }
    });

    const updatedStep: StepViewModel = {
      ...step,
      description: data.description,
      integrationSettingsIds: data.integrationSettingsIds,
      selectionStrategy: data.integrationSettingsIds.length > 1 ? data.selectionStrategy : null,
      stepTimeoutInMinutes: data.stepTimeoutInMinutes,
      onTimeoutExceededAction: data.onTimeoutExceededAction || undefined,
      onFatalErrorAction: data.onFatalErrorAction || undefined,
      waterfallRules: validRules.length > 0 ? validRules : null,
      multipleStepExecutionMode: data.multipleStepExecutionMode || MultipleStepExecutionMode.First,
    };

    try {
      await onSave(updatedStep);
      onClose();
    } catch (error) {
      noop(error);
    }
  };

  if (!step) return null;

  const vendorName = integration?.name || (isLoadingIntegration ? 'Loading...' : 'Unknown Vendor');
  const stepDescription = step.description || 'Untitled Step';

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header={
        <div className="flex items-center gap-2">
          <i className="pi pi-pencil"></i>
          <h3 className="m-0">Edit Step Details</h3>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" className="p-button-text" onClick={onClose} />
          <Button 
            type="button" 
            label="Save" 
            disabled={!isValid || (!isDirty && !hasRuleChanges && !hasExistingRules)} 
            onClick={handleSubmit(handleFormSubmit)}
          />
        </div>
      }
      style={{ width: '90vw', maxWidth: '800px' }}
      modal
      dismissableMask
    >
      <form
        id="step-details-form"
        noValidate
        className="p-fluid p-input-filled"
      >
        <div className="p-4">
          <div className="mb-4">
            <label className="block font-medium mb-2">
              Vendors <span className="text-red-500">*</span>
            </label>
            <Controller
              name="integrationSettingsIds"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  id={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={vendorOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select vendors"
                  className={`w-full ${errors.integrationSettingsIds ? 'p-invalid' : ''}`}
                  display="chip"
                />
              )}
            />
            {errors.integrationSettingsIds && (
              <small className="p-error block mt-1">{errors.integrationSettingsIds.message}</small>
            )}
          </div>

          {selectedVendorIds && selectedVendorIds.length > 1 && (
            <div className="mb-4">
              <FormDropdown
                control={control}
                errors={errors}
                field="selectionStrategy"
                label="Selection Strategy"
                options={selectionStrategyOptions}
                required
                placeholder="Select a strategy"
              />
              <small className="text-gray-500 block mt-1">
                <i className="pi pi-info-circle mr-1"></i>
                Round Robin cycles through vendors in order across executions
              </small>
            </div>
          )}

          <div className="mb-4">
            <FormInputText
              control={control}
              errors={errors}
              field="description"
              label="Description"
              placeholder="Enter step description"
              required
            />
          </div>

          {selectedVendorId > 0 && (
            <>
              <hr className="my-4 border-t border-gray-300" />

              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="font-medium" style={{ display: 'block', marginBottom: '0' }}>
                    Rules
                  </label>
                  <div className="flex items-center gap-2">
                    <Menu model={addRuleMenuItems} popup ref={addRuleMenuRef} />
                    <Button
                      type="button"
                      label="Add Rule"
                      icon="pi pi-plus"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addRuleMenuRef.current?.toggle(e);
                      }}
                    />
                  </div>
                </div>

                {rules.length === 0 ? (
                  <div className="text-gray-500 text-sm italic p-3 border border-dashed border-gray-300 rounded">
                    No rules added. Click "Add Rule" to add a step condition, exit condition, or request setting.
                  </div>
                ) : (
                rules.map((rule, index) => {
                  const ruleKey = ruleKeys[index] ?? `rule-${index}`;
                  return (
                  <div key={ruleKey} className="mb-4 p-4 border border-gray-300 rounded">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-700">
                          {getRuleTypeLabel(rule.ruleType)}
                        </span>
                      </div>
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger p-button-sm"
                        onClick={() => handleDeleteRule(index)}
                        tooltip="Delete rule"
                      />
                    </div>

                    {rule.ruleType === WaterfallRuleType.RequestSetting ? (
                      <OverrideSettingForm
                        initialValue={rule.overrideSetting || undefined}
                        onChange={(setting) => handleUpdateRuleOverrideSetting(index, setting)}
                        showDelete={false}
                        vendorName={integration?.vendor}
                      />
                    ) : (
                      <TabView 
                        activeIndex={ruleTabStates[ruleKey] ?? 0} 
                        onTabChange={(e) => setRuleTabStates({ ...ruleTabStates, [ruleKey]: e.index })}
                      >
                        <TabPanel header="Editor">
                          <RuleGroup
                            group={rule.expression || {
                              logicalOperator: LogicalOperator.And,
                              expressions: null,
                              isNot: false,
                            }}
                            level={0}
                            onUpdate={(expression) => handleUpdateRuleExpression(index, expression)}
                            fieldMetadata={filteredWaterfallContextFields}
                            rightFieldMetadata={filteredRequestValueTargetFields}
                            operators={operators}
                          />
                        </TabPanel>
                        <TabPanel header="JSON">
                          <pre className="bg-gray-100 p-3 border-round overflow-auto" style={{ maxHeight: '400px' }}>
                            {JSON.stringify(rule.expression || {
                              logicalOperator: LogicalOperator.And,
                              expressions: null,
                              isNot: false,
                            }, null, 2)}
                          </pre>
                        </TabPanel>
                      </TabView>
                    )}


                    {rule.ruleType !== WaterfallRuleType.RequestSetting && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <label className="font-medium text-sm mb-2 block">Override Setting (Optional)</label>
                        <OverrideSettingForm
                          initialValue={rule.overrideSetting || undefined}
                          onChange={(setting) => handleUpdateRuleOverrideSetting(index, setting)}
                          showDelete={false}
                          vendorName={integration?.vendor}
                        />
                      </div>
                    )}
                  </div>
                );
                })
              )}
              </div>
            </>
          )}

          <hr className="my-4 border-t border-gray-300" />

          <div className="mb-4">
            <FormInputNumber
              control={control}
              errors={errors}
              field="stepTimeoutInMinutes"
              label="Step Timeout (Minutes)"
              placeholder="Enter timeout in minutes"
            />
          </div>

          <div className="mb-4">
            <FormRadioGroup
              control={control}
              errors={errors}
              field="onTimeoutExceededAction"
              label="On Timeout Exceeded"
              options={actionOptions}
            />
          </div>

          <div className="mb-4">
            <FormRadioGroup
              control={control}
              errors={errors}
              field="onFatalErrorAction"
              label="On Fatal Error"
              options={actionOptions}
            />
          </div>

          <div className="mb-6">
            <FormDropdown
              control={control}
              errors={errors}
              field="multipleStepExecutionMode"
              label="Child Step Execution Mode"
              options={multipleStepExecutionModeOptions}
              placeholder="Select execution mode"
            />
            <small className="text-gray-500 block mt-1">
              <i className="pi pi-info-circle mr-1"></i>
              First: Only the first matching child step executes. All: All matching child steps execute (enables parallel/skip execution).
            </small>
          </div>

        </div>
      </form>
    </Dialog>
  );
};
